<script setup lang="ts">
import type { CustomPricingContext } from "@classes-service-types/index.ts";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";
import Toast from "primevue/toast";
import { useToast } from "primevue/usetoast";
import { computed, ref, watch } from "vue";
import { useBulkUpdateCustomPricing } from "@/providers/customPricing/useBulkUpdateCustomPricing";
import { useRowSelection } from "../composables/useRowSelection";
import {
  BULK_UPDATE_BANNER_STYLES,
  BULK_UPDATE_TOAST_GROUP,
  TOAST_LIFE_FAILURE_MS,
  TOAST_LIFE_SUCCESS_MS,
} from "../constants";
import { bulkScholarshipTitleSchema } from "../titleSchema";
import type { BulkUpdateResult, BulkUpdateSeverity, FailedRow } from "../types";
import { describeFailure } from "../utils";

defineOptions({ name: "BulkUpdateModal" });

const props = withDefaults(defineProps<{ visible: boolean }>(), { visible: false });

const emit = defineEmits<(e: "update:visible", value: boolean) => void>();

const toast = useToast();
const { selectedIds, selectedRows, selectedCount, hasSelection, clearSelection } = useRowSelection();
const { mutate: bulkUpdate, isPending } = useBulkUpdateCustomPricing();

const title = ref<string>("");
const failedRows = ref<FailedRow[]>([]);
const result = ref<BulkUpdateResult | undefined>(undefined);

const alreadyNamedCount = computed<number>(() => selectedRows.value.filter((row) => Boolean(row.title)).length);

const trimmedTitle = computed<string>(() => title.value.trim());
const titleValidation = computed(() => bulkScholarshipTitleSchema.safeParse(trimmedTitle.value));

// Stay quiet until something has been typed: an untouched field is not yet an error.
const titleError = computed<string | undefined>(() =>
  trimmedTitle.value.length > 0 && !titleValidation.value.success
    ? titleValidation.value.error.issues[0]?.message
    : undefined,
);

const canApply = computed<boolean>(() => titleValidation.value.success && hasSelection.value);
const resultStyle = computed(() => (result.value ? BULK_UPDATE_BANNER_STYLES[result.value.severity] : undefined));

const close = (): void => {
  emit("update:visible", false);
};

const finish = (): void => {
  clearSelection();
  close();
};

/** The modal banner and the toast say the same thing because they are set from the same value. */
const report = (severity: BulkUpdateSeverity, message: string): void => {
  result.value = { severity, message };
  toast.add({
    severity,
    summary: "Custom Pricing",
    detail: message,
    group: BULK_UPDATE_TOAST_GROUP,
    life: severity === "success" ? TOAST_LIFE_SUCCESS_MS : TOAST_LIFE_FAILURE_MS,
  });
};

const handleApply = (): void => {
  // Snapshot before the request: a successful apply invalidates the list, and a refetch that landed
  // first would prune these rows out of the selection and leave every failure reading as a bare id.
  const rowsById = new Map(selectedRows.value.map((row) => [row.id, row]));

  bulkUpdate(
    { overrideIds: [...selectedIds.value], title: trimmedTitle.value },
    {
      onSuccess: (response) => {
        // Count failures off the list we render, so the number and the list cannot disagree.
        const errors = response.errors ?? [];
        const updatedCount = response.updated_count ?? 0;
        failedRows.value = errors.map((error) => describeFailure(rowsById, error.override_id, error.details ?? ""));

        if (errors.length > 0) {
          report("warn", `${updatedCount} updated, ${errors.length} failed.`);
        } else {
          report("success", `${updatedCount} ${updatedCount === 1 ? "override" : "overrides"} updated.`);
          finish();
        }
      },
      onError: (error: Error & { cause?: { data?: { details?: { detail?: { message?: string } } } } }) => {
        const detail = error?.cause?.data?.details?.detail?.message || error.message;
        report("error", `Failed to apply scholarship name. ${detail}`);
      },
    },
  );
};

// A previous run's outcome must not read as this one's.
watch(
  () => props.visible,
  (isVisible) => {
    if (isVisible) {
      title.value = "";
      failedRows.value = [];
      result.value = undefined;
    }
  },
);
</script>

<template>
  <Toast position="bottom-right" :group="BULK_UPDATE_TOAST_GROUP" />
  <Dialog
    :visible="visible"
    @update:visible="close"
    header="Apply Scholarship Name"
    :modal="true"
    :closable="true"
    :style="{ width: '760px' }"
  >
    <div class="flex flex-col gap-4 py-2">
      <div class="text-sm text-gray-800" data-test-id="bulk-update-count">
        This will update
        <span class="font-semibold">{{ selectedCount }}</span>
        {{ selectedCount === 1 ? "override" : "overrides" }}.
      </div>

      <div
        v-if="alreadyNamedCount > 0"
        class="flex items-center gap-2 rounded-md border border-yellow-900/40 bg-yellow-100/20 px-3 py-2 text-sm text-gray-700"
      >
        <span class="material-symbols-outlined text-sm">warning</span>
        <span>
          {{ alreadyNamedCount }} of {{ selectedCount }} selected already have a scholarship name. Applying a new
          name replaces it.
        </span>
      </div>

      <div class="flex flex-col gap-1">
        <label for="bulk-scholarship-title" class="text-sm font-semibold text-gray-700">
          Scholarship Name
        </label>
        <InputText
          id="bulk-scholarship-title"
          v-model="title"
          placeholder="Scholarship name"
          class="w-full"
          :disabled="isPending"
        />
        <span v-if="titleError" class="text-sm text-red-700" data-test-id="bulk-update-title-error">
          {{ titleError }}
        </span>
      </div>

      <div
        v-if="result && resultStyle"
        class="flex items-center gap-2 rounded-md border px-3 py-2 text-sm"
        :class="resultStyle.classes"
        data-test-id="bulk-update-result"
      >
        <span class="material-symbols-outlined text-sm">{{ resultStyle.icon }}</span>
        <span>{{ result.message }}</span>
      </div>

      <!-- Fixed height so the dialog is the same size whether one override failed or fifty. -->
      <div
        v-if="failedRows.length > 0"
        class="flex h-60 flex-col gap-2 overflow-y-auto"
        data-test-id="bulk-update-failures"
      >
        <div
          v-for="failure of failedRows"
          :key="failure.overrideId"
          class="rounded border border-gray-300 px-3 py-2"
        >
          <div class="text-sm font-semibold text-gray-800">
            {{ failure.sessionName }}<span v-if="failure.sessionId">, {{ failure.sessionId }}</span>
          </div>
          <div v-if="failure.institutionName" class="text-sm text-gray-600">
            {{ failure.institutionName }}<span v-if="failure.institutionId">, {{ failure.institutionId }}</span>
          </div>
          <div class="text-xs text-gray-400">{{ failure.details }}</div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button label="Cancel" severity="secondary" @click="close" :disabled="isPending" />
        <Button
          label="Apply"
          data-test-id="bulk-update-confirm"
          @click="handleApply"
          :disabled="!canApply || isPending"
          :loading="isPending"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
</style>
